import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../App';
import api from '../services/api';

const STAGE_CFG = {
  'CRi non cloturé': { color: '#b91c1c', bg: 'rgba(185,28,28,0.12)', label: 'CRI non clôturé' },
  'CRI non cloturé': { color: '#b91c1c', bg: 'rgba(185,28,28,0.12)', label: 'CRI non clôturé' },
  'CR non conforme': { color: '#D35400', bg: 'rgba(211,84,0,0.12)',   label: 'CR non conforme' },
  'RDVNH':           { color: '#7B241C', bg: 'rgba(123,36,28,0.15)',  label: 'RDVNH' },
};

function stageCfg(s) {
  return STAGE_CFG[s] || { color: '#8b92a5', bg: 'rgba(139,146,165,0.15)', label: s };
}

const TYPO_COLORS = {
  'RAC_I': { color: '#185FA5', bg: 'rgba(24,95,165,0.12)' },
  'RAC_C': { color: '#185FA5', bg: 'rgba(24,95,165,0.12)' },
  'RAC_F': { color: '#a855f7', bg: 'rgba(168,85,247,0.12)' },
  'RAC_P': { color: '#06b6d4', bg: 'rgba(6,182,212,0.12)' },
  'SAV':   { color: '#b45309', bg: 'rgba(180,83,9,0.12)' },
};
function TypoBadge({ typo }) {
  if (!typo) return null;
  const cfg = TYPO_COLORS[typo] || { color: '#8b92a5', bg: 'rgba(139,146,165,0.15)' };
  return <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 10, color: cfg.color, background: cfg.bg, whiteSpace: 'nowrap' }}>{typo}</span>;
}

const SMS_TYPE_CFG = {
  cri_j1:          { label: 'SMS J+1',           color: '#b45309', bg: 'rgba(180,83,9,0.12)' },
  cri_j2:          { label: 'SMS J+2',           color: '#c2410c', bg: 'rgba(194,65,12,0.12)' },
  cri_j3:          { label: 'SMS J+3',           color: '#b91c1c', bg: 'rgba(185,28,28,0.14)' },
  rdvnh:           { label: 'SMS RDVNH',         color: '#7B241C', bg: 'rgba(123,36,28,0.14)' },
  cr_non_conforme: { label: 'SMS CR non conf.',  color: '#D35400', bg: 'rgba(211,84,0,0.12)' },
};

function SmsBadges({ sms }) {
  if (!sms || sms.length === 0) return null;
  // Dédoublonner par type (garder le plus récent)
  const byType = {};
  sms.forEach(s => { if (!byType[s.type] || s.at > byType[s.type].at) byType[s.type] = s; });
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
      {Object.values(byType).map(s => {
        const cfg = SMS_TYPE_CFG[s.type] || { label: s.type, color: '#8b92a5', bg: 'rgba(139,146,165,0.15)' };
        return (
          <span key={s.type} title={`Envoyé le ${s.date}${s.phone ? ' → ' + s.phone : ''}`} style={{
            fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 20,
            color: cfg.color, background: cfg.bg, whiteSpace: 'nowrap',
            border: `1px solid ${cfg.color}44`,
          }}>
            📱 {cfg.label}
          </span>
        );
      })}
    </div>
  );
}

function RetardBadge({ jours }) {
  if (jours === null || jours === undefined) return <span style={{ color: 'var(--text3)', fontSize: 12 }}>—</span>;
  const color = jours >= 3 ? '#7B241C' : jours >= 1 ? '#b91c1c' : '#b45309';
  const bg    = jours >= 3 ? 'rgba(123,36,28,0.18)' : jours >= 1 ? 'rgba(185,28,28,0.12)' : 'rgba(180,83,9,0.12)';
  return (
    <span style={{ fontSize: 12, fontWeight: 800, padding: '3px 10px', borderRadius: 20, color, background: bg, whiteSpace: 'nowrap' }}>
      ⏱ {jours}j de retard
    </span>
  );
}

export default function SuiviCRI() {
  const { admin } = useAuth();
  const [interventions, setInterventions] = useState([]);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState('');
  const [search, setSearch]               = useState('');
  const [filterStage, setFilterStage]     = useState('');
  const [expanded, setExpanded]           = useState(null);

  const load = () => {
    setLoading(true); setError('');
    api.get('/admin/suivi-cri')
      .then(r => setInterventions(r.data.interventions || []))
      .catch(() => setError('Erreur récupération Zoho'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const stages = [...new Set(interventions.map(i => i.stage).filter(Boolean))];

  const filtered = interventions.filter(i => {
    if (filterStage && i.stage !== filterStage) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!`${i.ref_commande} ${i.ref_work_order} ${i.client_final} ${i.technicien} ${i.ville}`.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const cri_count      = interventions.filter(i => i.stage === 'CRi non cloturé' || i.stage === 'CRI non cloturé').length;
  const crconf_count   = interventions.filter(i => i.stage === 'CR non conforme').length;
  const rdvnh_count    = interventions.filter(i => i.stage === 'RDVNH').length;

  return (
    <>
      <div className="topbar">
        <div>
          <div className="topbar-title">Suivi CRI</div>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>
            Interventions non clôturées — CRI &amp; RDVNH
          </div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          <button className="btn btn-secondary btn-sm" onClick={load} disabled={loading}>↻ Actualiser</button>
        </div>
      </div>

      {/* Compteurs */}
      {!loading && (
        <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', background: 'var(--bg2)' }}>
          {[
            { label: 'CRI non clôturés',  count: cri_count,    color: '#b91c1c', bg: '#fee2e2' },
            { label: 'CR non conformes',  count: crconf_count, color: '#D35400', bg: '#fff3e0' },
            { label: 'RDVNH',             count: rdvnh_count,  color: '#7B241C', bg: '#fde8e8' },
            { label: 'Total',             count: interventions.length, color: 'var(--text)', bg: 'var(--bg3)' },
          ].map(s => (
            <div key={s.label} style={{ padding: '12px 28px', borderRight: '1px solid var(--border)', textAlign: 'center' }}>
              <div style={{ fontSize: 24, fontWeight: 900, color: s.color }}>{s.count}</div>
              <div style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 600, marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filtres */}
      <div style={{ padding: '10px 24px', borderBottom: '1px solid var(--border)', background: 'var(--bg2)', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          className="form-input"
          style={{ width: 220, fontSize: 12 }}
          placeholder="🔍 Réf, client, technicien…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <div style={{ display: 'flex', gap: 4 }}>
          <button onClick={() => setFilterStage('')} style={{
            fontSize: 11, fontWeight: 600, padding: '4px 12px', borderRadius: 20, cursor: 'pointer',
            background: !filterStage ? 'rgba(24,95,165,0.15)' : 'var(--bg3)',
            color: !filterStage ? '#185FA5' : 'var(--text3)',
            border: `1px solid ${!filterStage ? '#185FA5' : 'transparent'}`,
          }}>Tous</button>
          {stages.map(s => {
            const cfg = stageCfg(s);
            const on = filterStage === s;
            return (
              <button key={s} onClick={() => setFilterStage(on ? '' : s)} style={{
                fontSize: 11, fontWeight: 600, padding: '4px 12px', borderRadius: 20, cursor: 'pointer',
                background: on ? cfg.bg : 'var(--bg3)',
                color: on ? cfg.color : 'var(--text3)',
                border: `1px solid ${on ? cfg.color : 'transparent'}`,
              }}>{cfg.label}</button>
            );
          })}
        </div>
        <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text3)' }}>
          {filtered.length} intervention{filtered.length > 1 ? 's' : ''}
        </span>
      </div>

      {/* Table */}
      <div style={{ flex: 1, overflow: 'auto', padding: '0 24px 24px' }}>
        {loading && <div style={{ padding: 60, textAlign: 'center', color: 'var(--text3)' }}>Chargement…</div>}
        {error && <div style={{ margin: '20px 0', padding: '12px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#ef4444', fontSize: 13 }}>{error}</div>}

        {!loading && filtered.length === 0 && (
          <div style={{ padding: 60, textAlign: 'center', color: 'var(--text3)', fontSize: 14 }}>
            {interventions.length === 0 ? '✅ Aucune intervention en retard de clôture' : 'Aucun résultat'}
          </div>
        )}

        {!loading && filtered.length > 0 && (
          <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {filtered.map(i => {
              const cfg = stageCfg(i.stage);
              const isOpen = expanded === i.id;
              return (
                <div key={i.id} style={{
                  border: `1px solid ${i.retard_jours >= 3 ? '#fecaca' : 'var(--border)'}`,
                  borderLeft: `4px solid ${cfg.color}`,
                  borderRadius: 10, background: 'var(--bg2)',
                  overflow: 'hidden',
                }}>
                  {/* Ligne principale */}
                  <div
                    onClick={() => setExpanded(isOpen ? null : i.id)}
                    style={{ padding: '12px 16px', cursor: 'pointer', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}
                  >
                    {/* Stage badge */}
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20, color: cfg.color, background: cfg.bg, whiteSpace: 'nowrap', flexShrink: 0 }}>
                      {cfg.label}
                    </span>

                    {/* Retard */}
                    <RetardBadge jours={i.retard_jours} />

                    {/* Ref */}
                    <span style={{ fontFamily: 'var(--mono)', fontWeight: 700, fontSize: 12, color: 'var(--accent)', flexShrink: 0 }}>{i.ref_commande || '—'}</span>

                    {/* Typo */}
                    <TypoBadge typo={i.typo_raccordement} />

                    {/* Client */}
                    <span style={{ fontWeight: 600, fontSize: 13, flex: 1, minWidth: 120 }}>{i.client_final || '—'}</span>

                    {/* Ville + CP */}
                    <span style={{ fontSize: 12, color: 'var(--text3)', flexShrink: 0 }}>{[i.ville, i.code_postal].filter(Boolean).join(' ')}</span>

                    {/* Date */}
                    <span style={{ fontSize: 12, color: 'var(--text2)', flexShrink: 0 }}>
                      {i.date_intervention || '—'}{i.heure_intervention ? ` à ${i.heure_intervention}` : ''}
                    </span>

                    {/* Technicien */}
                    <span style={{ fontSize: 12, color: 'var(--text3)', flexShrink: 0 }}>👷 {i.technicien || '—'}</span>

                    {/* Expand */}
                    <span style={{ marginLeft: 'auto', color: 'var(--text3)', fontSize: 12, flexShrink: 0 }}>{isOpen ? '▲' : '▼'}</span>
                  </div>
                  {i.sms_envoyes?.length > 0 && (
                    <div style={{ padding: '0 16px 10px' }}>
                      <SmsBadges sms={i.sms_envoyes} />
                    </div>
                  )}

                  {/* Détail expandé */}
                  {isOpen && (
                    <div style={{ padding: '0 16px 14px', borderTop: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 8, marginTop: 0 }}>
                      <div style={{ paddingTop: 12, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
                        {i.ref_work_order && (
                          <div>
                            <div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 700, textTransform: 'uppercase', marginBottom: 2 }}>Réf. Work Order</div>
                            <div style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{i.ref_work_order}</div>
                          </div>
                        )}
                        <div>
                          <div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 700, textTransform: 'uppercase', marginBottom: 2 }}>Technicien</div>
                          <div style={{ fontSize: 12 }}>{i.technicien || '—'}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 700, textTransform: 'uppercase', marginBottom: 2 }}>Date intervention</div>
                          <div style={{ fontSize: 12 }}>{i.date_intervention || '—'}{i.heure_intervention ? ` à ${i.heure_intervention}` : ''}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 700, textTransform: 'uppercase', marginBottom: 2 }}>Ville</div>
                          <div style={{ fontSize: 12 }}>{[i.code_postal, i.ville].filter(Boolean).join(' ') || '—'}</div>
                        </div>
                      </div>
                      {(i.commentaire_interne || i.commentaire_covage) && (
                        <div style={{ paddingTop: 8, borderTop: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {i.commentaire_interne && (
                            <div>
                              <div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 700, textTransform: 'uppercase', marginBottom: 2 }}>Commentaire interne</div>
                              <div style={{ fontSize: 12, color: 'var(--text2)' }}>{i.commentaire_interne}</div>
                            </div>
                          )}
                          {i.commentaire_covage && (
                            <div>
                              <div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 700, textTransform: 'uppercase', marginBottom: 2 }}>Commentaire COVAGE</div>
                              <div style={{ fontSize: 12, color: 'var(--text2)' }}>{i.commentaire_covage}</div>
                            </div>
                          )}
                        </div>
                      )}
                      <div style={{ paddingTop: 8 }}>
                        <Link
                          to={`/dossier/${i.id}`}
                          className="btn btn-secondary btn-sm"
                          style={{ textDecoration: 'none', fontSize: 12, marginRight: 8 }}
                          onClick={e => e.stopPropagation()}
                        >Ouvrir la fiche</Link>
                        <a
                          href={`https://crm.zoho.eu/crm/tab/Potentials/${i.id}`}
                          target="_blank" rel="noreferrer"
                          className="btn btn-secondary btn-sm"
                          style={{ textDecoration: 'none', fontSize: 12 }}
                          onClick={e => e.stopPropagation()}
                        >🔗 Ouvrir dans Zoho OT</a>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
